import {
	CasperIconComponent,
	Flex,
	ForwardIcon,
	NavigationListButton,
	Text,
	ThemeColorVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { NavLink } from "react-router";
import { ReactNode } from "react";
import { useUserContext } from "../../hooks/use-user-context";
import { hasPermissionOnSystem } from "../../models/permission";

type INavButtonProps = {
	title: string;
	subTitle?: string;
	titleColor?: ThemeColorVariable;
	onClick?: () => void;
	leftIcon?: CasperIconComponent;
	rightElementText?: string;
	rightElement?: ReactNode;
	to?: string;
	key?: string;
	testId?: string;
};

type INavButtonWithIconProps = Omit<INavButtonProps, "rightElement"> & {
	rightIcon?: CasperIconComponent | null;
	requiredPermission?: string[];
};

const NavButton = ({
	title,
	leftIcon: LeftIcon,
	subTitle,
	rightElement,
	rightElementText,
	to,
	titleColor,
	onClick,
	key,
	testId,
}: INavButtonProps) => {
	return (
		<NavigationListButton
			onClick={onClick}
			key={key}
			paddingStart={ThemeSpaceVariable.None}
			paddingLeft={ThemeSpaceVariable.None}
			paddingEnd={ThemeSpaceVariable.None}
			data-testid={testId}
		>
			<NavLink to={to ?? ""}>
				<Flex align="center" justifyContent="space-between">
					<Flex direction={"column"}>
						<Flex align="center" gap={ThemeSpaceVariable.Small}>
							{LeftIcon && <LeftIcon fontSize="20px" color={titleColor ?? undefined} />}
							<Text color={titleColor ?? ThemeColorVariable.OnSurface} fontWeight={"medium"}>
								{title}
							</Text>
						</Flex>
						{subTitle && (
							<Text color={ThemeColorVariable.OnBackgroundSubdued} marginLeft={ThemeSpaceVariable.Large}>
								{subTitle}
							</Text>
						)}
					</Flex>
					<Flex
						direction={"row"}
						gap={ThemeSpaceVariable.Small}
						justifyContent={"center"}
						alignItems={"center"}
					>
						{rightElementText && (
							<Text color={ThemeColorVariable.OnBackgroundSubdued}>{rightElementText}</Text>
						)}
						{rightElement}
					</Flex>
				</Flex>
			</NavLink>
		</NavigationListButton>
	);
};

export const NavButtonWithIcon = ({ rightIcon, requiredPermission, ...props }: INavButtonWithIconProps) => {
	const { systems, permissions } = useUserContext();

	if (
		requiredPermission &&
		!systems.some((s) => requiredPermission.some((p) => hasPermissionOnSystem(permissions, s.name, p)))
	) {
		return false;
	}

	const RightIcon = rightIcon === null ? null : (rightIcon ?? ForwardIcon);

	return (
		<NavButton
			rightElement={RightIcon && <RightIcon fontSize="20px" color={ThemeColorVariable.OnBackgroundSubdued} />}
			{...props}
		/>
	);
};
